// Turns a JSON Schema node into a TypeScript type string, used to generate the
// types the CodeMirror expression editor shows for autocomplete. This is kept
// deliberately simple: it only handles the property types that show up in block
// method schemas (primitives, objects, arrays, and oneOf lists of const values
// used as enums). Anything it does not recognize becomes `unknown`.

type SchemaNode = Record<string, unknown>;

function jsonSchemaToTsType(schema: unknown): string {
  if (!schema || typeof schema !== 'object') return 'unknown';
  const s = schema as SchemaNode;

  // A oneOf made of `const` values is the enum pattern. Turn it into a union of
  // string or number literals (for example 'a' | 'b').
  if (Array.isArray(s.oneOf)) {
    const consts = (s.oneOf as unknown[])
      .filter((opt): opt is SchemaNode => typeof opt === 'object' && opt !== null && 'const' in opt)
      .map((opt) => opt.const);
    if (consts.length > 0) {
      if (consts.every((c) => typeof c === 'string')) {
        return consts.map((c) => `'${c as string}'`).join(' | ');
      }
      if (consts.every((c) => typeof c === 'number')) {
        return consts.join(' | ');
      }
    }
  }

  switch (s.type) {
    case 'string':
      return 'string';
    case 'number':
    case 'integer':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'null':
      return 'null';
    case 'array': {
      const items = s.items;
      if (items) return `${jsonSchemaToTsType(items)}[]`;
      return 'unknown[]';
    }
    case 'object': {
      const props = s.properties as Record<string, unknown> | undefined;
      const required = Array.isArray(s.required)
        ? new Set(s.required as string[])
        : new Set<string>();
      if (!props || Object.keys(props).length === 0) return 'Record<string, unknown>';
      const parts = Object.entries(props).map(([key, val]) => {
        const opt = required.has(key) ? '' : '?';
        return `${key}${opt}: ${jsonSchemaToTsType(val)}`;
      });
      return `{ ${parts.join('; ')} }`;
    }
    default:
      return 'unknown';
  }
}

// Pulls the `methodPayload` property out of a block method's JSON Schema and
// returns its TypeScript type string. Returns null when the schema has no payload
// shape, which is the case for methods that take no arguments.
export function methodSchemaToPayloadTsType(schema: unknown): string | null {
  if (!schema || typeof schema !== 'object') return null;
  const s = schema as SchemaNode;
  const props = s.properties as SchemaNode | undefined;
  const payloadSchema = props?.methodPayload;
  if (!payloadSchema) return null;
  const tsType = jsonSchemaToTsType(payloadSchema);
  // These results carry no useful shape, so treat them as "no defined params".
  if (tsType === 'Record<string, unknown>' || tsType === 'unknown' || tsType === '{}') return null;
  return tsType;
}
